import { type FormEvent, useState } from 'react';
import { confirmSignIn, fetchAuthSession, signIn, signOut } from 'aws-amplify/auth';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { authConfig } from '@/config/auth.ts';

// Persist the "not authorized" error across the sign-in/sign-out bounce:
// Amplify emits `signedIn` before we can verify groups, which can navigate
// away and remount this form. sessionStorage survives that round trip.
const LOGIN_ERROR_KEY = 'wellspring:login_error';

export function readPersistedLoginError(): string | null {
  const error = sessionStorage.getItem(LOGIN_ERROR_KEY);
  if (error) {
    sessionStorage.removeItem(LOGIN_ERROR_KEY);
  }
  return error;
}

/**
 * Check that a freshly signed-in session belongs to a Wellspring user.
 * Non-SSO (native Cognito) users must be in at least one group with the
 * Wellspring- prefix; anyone else is signed out immediately.
 */
export async function verifyWellspringAccess(): Promise<boolean> {
  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
  const groups = (payload?.['cognito:groups'] as string[] | undefined) ?? [];
  const prefix = authConfig.groupPrefix ?? 'Wellspring-';

  if (groups.some((group) => group.startsWith(prefix))) {
    return true;
  }

  sessionStorage.setItem(
    LOGIN_ERROR_KEY,
    'Your account is not authorized to access Wellspring. Contact your Metron administrator.',
  );
  await signOut();
  return false;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    // Cognito hides user-existence errors, so NotAuthorizedException covers
    // both bad usernames and bad passwords.
    if (err.name === 'NotAuthorizedException') {
      return 'Incorrect username or password.';
    }
    return err.message;
  }
  return 'Sign-in failed. Please try again.';
}

/**
 * Username/password sign-in against the shared Cognito user pool, for
 * non-Metron (non-SSO) users. Handles the NEW_PASSWORD_REQUIRED challenge
 * that admin-created users hit on first sign-in.
 */
export function CredentialsLoginForm() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [error, setError] = useState<string | null>(readPersistedLoginError);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignInResult = async (
    result: { isSignedIn: boolean; nextStep: { signInStep: string } },
  ) => {
    if (result.isSignedIn) {
      await verifyWellspringAccess();
      // On success the AuthProvider's Hub listener picks up the signedIn
      // event and the login page redirects; on failure the persisted error
      // is shown after the sign-out bounce.
      return;
    }
    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setNeedsNewPassword(true);
      return;
    }
    // ponytail: MFA and other challenges unsupported until MFA work lands
    setError(`Unsupported sign-in challenge: ${result.nextStep.signInStep}`);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (needsNewPassword) {
        await handleSignInResult(await confirmSignIn({ challengeResponse: newPassword }));
      } else {
        await handleSignInResult(await signIn({ username, password }));
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      {needsNewPassword
        ? (
          <div className='space-y-2'>
            <p className='text-sm text-muted-foreground'>
              You must choose a new password before continuing.
            </p>
            <Label htmlFor='login-new-password'>New password</Label>
            <Input
              id='login-new-password'
              type='password'
              autoComplete='new-password'
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
        )
        : (
          <>
            <div className='space-y-2'>
              <Label htmlFor='login-username'>Email</Label>
              <Input
                id='login-username'
                type='email'
                autoComplete='username'
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='login-password'>Password</Label>
              <Input
                id='login-password'
                type='password'
                autoComplete='current-password'
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </>
        )}

      {error && (
        <p role='alert' className='text-sm text-destructive'>
          {error}
        </p>
      )}

      <Button
        type='submit'
        variant='outline'
        className='w-full'
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );
}
